import { OpenCashoutModalType } from "@data-types/my-bets/OpenCashoutModal";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { Bet } from "@models";
import { history } from "@state/utils";
import { getCurrentCulture, getWalletByShopIdAndAccountTypeId } from "@utils";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { getEventIdsFromMyBets } from "@utils/my-bets/helpers";
import { action, computed, observable, runInAction } from "mobx";

import { LoaderStore } from "../common";
import { RootStore } from "../types";
import { ActiveEventsStore } from "./ActiveEventsStore";
import { BetCancelStore } from "./BetCancelStore";
import { CashOutSubmitType } from "./BetDetailsModalStore";
import { CashoutStore } from "./CashoutStore";
import { ReuseBetSlipStore } from "./ReuseBetSlipStore";
import ShareBetSlipStore from "./ShareBetSlipStore";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const BetSlipService = lazy(loadFailPath, () =>import("@services/bet-slip/BetSlipService"));

export class BetSlipReceiptModalStore {
	rootStore: RootStore;
	loader: LoaderStore;
	cashoutStore: CashoutStore;
	reuseBetSlipStore: ReuseBetSlipStore;
	betCancelStore: BetCancelStore;
	shareBetSlipStore: ShareBetSlipStore;
	activeEventsStore: ActiveEventsStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;

		this.activeEventsStore = new ActiveEventsStore(rootStore);
		this.cashoutStore = new CashoutStore(this);

		this.reuseBetSlipStore = new ReuseBetSlipStore(rootStore);
		this.betCancelStore = new BetCancelStore(rootStore, this);
		this.shareBetSlipStore = new ShareBetSlipStore(rootStore);

		this.loader = new LoaderStore();
	}

	@observable isModalOpen: boolean = false;

	@observable walletId: string = "";
	@observable isStoreInitialized: boolean = false;
	@observable currentBet: Bet;
	@observable betCombinationsMap = observable.map();

	@observable isCashoutOpen: boolean = false;
	@observable isBetCancelOpen: boolean = false;
	@observable isAutoSellOpen: boolean = false;
	@observable isReuseOpen: boolean = false;
	@observable isShareActive: boolean = false;

	@computed get betsList() {
		if (this.currentBet != null) {
			return [this.currentBet];
		}

		return [];
	}

	@computed get isMyBetsLoading(): boolean {
		return false;
	}

	@action.bound
	async onInitialize(walletId: string) {
		this.loader.suspend();
		this.walletId = walletId;

		await this.cashoutStore.onInitialize();
		runInAction(() => {
			this.loader.resume();
			this.isStoreInitialized = true;
		});
	}

	@action.bound
	async setCurrentBet(bet: Bet) {
		this.loader.suspend();

		const eventIdsFromBet = getEventIdsFromMyBets([bet]);
		this.activeEventsStore.fetchActiveEvents(eventIdsFromBet);

		if (bet.combinationBettingOffersCount > 1) {
			await this.initBetCombinationForBetSlip(bet);
		}

		runInAction(() => {
			this.currentBet = bet;
			this.loader.resume();
		});
	}

	@action.bound
	async initBetCombinationForBetSlip(bet: Bet) {
		if (this.betCombinationsMap.has(bet.id)) {
			return;
		}

		this.betCombinationsMap.set(bet.id, null);
		let userAccount = getWalletByShopIdAndAccountTypeId(
			bet.shopId,
			bet.bettingAccountTypeId,
			this.rootStore.userAuthStore.userAccounts || []
		);
		let context = this.rootStore.userAuthStore.mapUserIdToUserContext(
			bet.userId
		);
		if (userAccount) {
			context = createApiRequestOptions(
				RequestContextType.Wallet,
				userAccount.id
			);
		}
		const combinations = await (
			await BetSlipService
		).default.getSystemCombinations(bet.id, context);

		this.betCombinationsMap.set(bet.id, combinations);
	}

	@action.bound
	refresh() {
		if (this.currentBet?.id == null) {
			return;
		}

		this.setCurrentBet(this.currentBet);
	}

	@action.bound
	openModal(bet: Bet, walletId: string) {
		this.isModalOpen = true;
		this.onInitialize(walletId);

		this.setCurrentBet(bet);
	}

	@action.bound
	closeModal() {
		this.isModalOpen = false;

		this.onDispose();
	}

	@action.bound
	async submitCashout(data: CashOutSubmitType) {
		await this.cashoutStore.cashout(data, null);

		this.closeCashout();

		this.closeModal();
		history.replace(`/${getCurrentCulture()}/my-bets/closed`);
	}

	@action.bound
	openCashout(data: OpenCashoutModalType) {
		this.isCashoutOpen = true;

		this.cashoutStore.openBetDetailsCashout(data);
	}

	@action.bound
	closeCashout() {
		this.isCashoutOpen = false;
	}

	@action.bound
	cancelCashout() {
		this.closeCashout();

		this.cashoutStore.closeBetDetailsCashout();
	}

	@action.bound
	onBetCancelOpen() {
		this.isBetCancelOpen = true;
	}

	@action.bound
	onBetCancelCloseStay() {
		this.isBetCancelOpen = false;
	}

	@action.bound
	onBetCancelClose() {
		this.isBetCancelOpen = false;

		this.closeModal();
		history.replace(`/${getCurrentCulture()}/my-bets/closed`);
	}

	@action.bound
	onAutoSellOpen() {
		this.isAutoSellOpen = true;
	}

	@action.bound
	onAutoSellClose() {
		this.isAutoSellOpen = false;
	}

	@action.bound
	onReuseOpen() {
		this.isReuseOpen = true;
	}

	@action.bound
	onReuseClose() {
		this.isReuseOpen = false;
	}

	@action.bound
	onShareOpen() {
		this.isShareActive = true;
	}

	@action.bound
	onShareClose() {
		this.isShareActive = false;
	}

	@action.bound
	onDispose() {
		this.betCancelStore.onDispose();
		this.cashoutStore.onDispose();
		this.activeEventsStore.onDispose();
		this.betCombinationsMap.clear();
		this.isStoreInitialized = false;
		this.walletId = "";
	}
}
